import { useState, useEffect, useCallback, useRef } from 'react';
import type { FilterOption } from '@/types/dashboard';

// Deduplicate by display name — matches legacy setFilter:
// "if (requestConversation[i].Text === filter.Name) addTag = false"
function deduplicateFilters(filters: FilterOption[]): FilterOption[]
{
  const seen = new Set<string>();
  return filters.filter(f =>
  {
    if (seen.has(f.Name)) return false;
    seen.add(f.Name);
    return true;
  });
}

function loadFromStorage(storageKey: string): FilterOption[]
{
  try
  {
    const saved = localStorage.getItem(storageKey);
    if (saved)
    {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return deduplicateFilters(parsed);
    }
  }
  catch (e)
  {
    console.error('Error parsing saved filters for key:', storageKey, e);
  }
  return [];
}

export const useFilterPersistence = (storageKey: string) =>
{
  const [filtersSelected, setFiltersSelected] = useState<FilterOption[]>(() =>
    loadFromStorage(storageKey),
  );
  const [appliedFilters, setAppliedFilters] = useState<FilterOption[]>(() =>
    loadFromStorage(storageKey),
  );

  // Always-current ref to avoid stale closures in callbacks
  const filtersSelectedRef = useRef(filtersSelected);
  filtersSelectedRef.current = filtersSelected;

  // Synchronous derived state: when storageKey changes, reload filters immediately
  // during the render so consumers (e.g. DashboardGroups) see the correct value on
  // the first render after a dashboard switch — prevents a spurious API call with
  // stale filters from the previous dashboard.
  const [prevKey, setPrevKey] = useState(storageKey);
  if (prevKey !== storageKey)
  {
    setPrevKey(storageKey);
    const loaded = loadFromStorage(storageKey);
    setFiltersSelected(loaded);
    setAppliedFilters(loaded);
  }

  // Save applied filters to storage whenever they change.
  // Skip on initial mount (value already came from storage).
  const isMounted = useRef(false);
  useEffect(() =>
  {
    if (!isMounted.current)
    {
      isMounted.current = true;
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(deduplicateFilters(appliedFilters)));
  }, [storageKey, appliedFilters]);

  const handleFilterSelect = useCallback((option: FilterOption) =>
  {
    setFiltersSelected(prev =>
    {
      // Deduplicate by display name — matches legacy setFilter behavior
      if (prev.some(f => f.Name === option.Name)) return prev;
      return [...prev, option];
    });
  }, []);

  const handleFilterUnselect = useCallback((option: FilterOption) =>
  {
    setFiltersSelected(prev =>
      prev.filter(f => !(f.Id === option.Id && f.FieldName === option.FieldName)),
    );
  }, []);

  const handleApplyFilters = useCallback(() =>
  {
    // Read from ref to always get latest filtersSelected regardless of closure
    setAppliedFilters(filtersSelectedRef.current);
  }, []);

  const removeFiltersByFieldName = useCallback((fieldName: string) =>
  {
    setFiltersSelected(prev => prev.filter(f => f.FieldName !== fieldName));
  }, []);

  const overrideFilters = useCallback((filters: FilterOption[]) =>
  {
    const clean = deduplicateFilters(filters);
    setFiltersSelected(clean);
    setAppliedFilters(clean);
    localStorage.setItem(storageKey, JSON.stringify(clean));
  }, [storageKey]);

  return {
    filtersSelected,
    appliedFilters,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
    removeFiltersByFieldName,
    overrideFilters,
  };
};
